import { Step } from 'react-joyride';
import type React from 'react';

import { onboardingKnowledgeBase as kb } from './onboardingKnowledgeBase';

export type OnboardingExperience = 'none' | 'new' | 'returning' | 'whatsNew';
export type OnboardingReturnBand = 'recent' | 'moderate' | 'old' | null;
export type OnboardingMode = 'quick' | 'learn';

export type OnboardingContext = {
  isSharedEngagement?: boolean;
};

const LearnMore = ({ href }: { href: string }) => (
  <a href={href} target="_blank" rel="noreferrer" style={{ color: '#B95312', display: 'block', marginTop: 8 }}>
    Read the matching Knowledge Base article
  </a>
);

const step = (
  target: string,
  title: string,
  body: React.ReactNode,
  href?: string,
  placement: Step['placement'] = 'auto'
): Step => ({
  target,
  disableBeacon: true,
  placement,
  content: (
    <div>
      <strong style={{ display: 'block', marginBottom: 6 }}>{title}</strong>
      <span>{body}</span>
      {href && <LearnMore href={href} />}
    </div>
  )
});

const commonQuickSteps = (isSharedEngagement: boolean): Step[] => [
  isSharedEngagement
    ? step(
        '[data-tour="updates-menu"]',
        'Updates',
        'Respond to assigned Signals, add your own updates and see shared activity.'
      )
    : step(
        '[data-tour="collaborate-menu"]',
        'Collaborate',
        'Complete scheduled Workflow surveys and follow their review.'
      ),
  step(
    '[data-tour="strategy-menu"]',
    'Strategy',
    'Manage your Strategy’s setup, Deliverable Workflows and Signal schedule.'
  ),
  step('[data-tour="insights-menu"]', 'Insights', 'Explore evidence and progress across your Strategies.'),
  step(
    '[data-tour="profile"]',
    'Your profile',
    'Manage your details and restart this learning experience whenever you need it.'
  ),
  step('[data-tour="help-menu"]', 'Help', 'Open the weavr Knowledge Base for practical guidance.')
];

const pageSteps = (pathname: string): Step[] => {
  if (pathname.startsWith('/updates/review')) {
    return [
      step(
        '[data-tour="review-page"]',
        'Review submitted updates',
        'Check the Strategy, Deliverable, Signal and Member response before publishing.',
        kb.review
      ),
      step(
        '[data-tour="review-tabs"]',
        'Choose the update type',
        'Switch between additional updates and assigned Signal responses.'
      ),
      step(
        '[data-tour="review-list"]',
        'Review and publish',
        'Open an item to see its context, then approve or reject it using your organisation’s review rules.'
      )
    ];
  }

  if (pathname === '/updates' || pathname.startsWith('/updates?')) {
    return [
      step(
        '[data-tour="updates-page"]',
        'Updates',
        'This is where you respond to assigned Signals, share additional updates and follow progress.',
        kb.updates
      ),
      step(
        '[data-tour="update-activity"]',
        'Your update activity',
        'The donut shows completed and incomplete assigned responses. Select either result to filter the Focus Areas below.'
      ),
      step(
        '[data-tour="focus-area-section"]',
        'Focus Areas',
        'Only Focus Areas used by an active Strategy appear here. They group the work by the business area it supports.',
        kb.focusAreas
      ),
      step(
        '[data-tour="focus-area-counts"]',
        'Assigned and waiting counts',
        'Assigned includes Signals allocated directly to you or through a Deliverable Workflow where you are a Responder. Waiting is the assigned set from before now that still needs an update.'
      ),
      step(
        '[data-tour="deliverable-search"]',
        'Find a Deliverable',
        'Search by its number or name. Assigned Deliverables appear first; other active Deliverables appear grey below.'
      ),
      step(
        '[data-tour="assigned-deliverable"]',
        'Respond to an assigned Signal',
        'Open an assigned Deliverable and select the highlighted “Update due” question. Confirm the reporting period, add the response and submit it.',
        kb.respond
      ),
      step(
        '[data-tour="assigned-signal-toggle"]',
        'Add a Signal-specific update early',
        'Select “assigned Signals” to reveal grey questions that are not due. Choose one to add an unprompted update against that Signal and select the period it belongs to.'
      ),
      step(
        '[data-tour="unassigned-deliverable"]',
        'Add a Deliverable update',
        'Select a grey, unassigned Deliverable or its plus button to add an unprompted update to the Deliverable. This does not answer an assigned Signal.',
        kb.share
      ),
      step(
        '[data-tour="updates-feed"]',
        'Shared activity',
        'Use the feed filters to find updates. What you can see depends on the update’s visibility and your responsibilities.'
      )
    ];
  }

  if (pathname === '/collaborate' || pathname.startsWith('/collaborate?')) {
    return [
      step(
        '[data-tour="collaborate-page"]',
        'Collaborate',
        'Restricted engagement uses Collaborate for scheduled progress updates. Strategy configuration remains in the same Strategy pages used by every subscription.',
        kb.collaborate
      ),
      step(
        '[data-tour="collaborate-tabs"]',
        'Choose what to view',
        'Progress updates lists surveys you can respond to or approve. Calendar shows scheduled work, and Engagement shows activity.'
      ),
      step(
        '[data-tour="collaborate-progress"]',
        'Workflow progress updates',
        'A named Workflow groups its Responders, approval levels and assigned Signal periods. Open a row to complete the Signals available to you for that period.'
      ),
      step(
        '[data-tour="collaborate-progress"]',
        'Respond or approve',
        'Responders complete the survey first. Where approval levels exist, any one Approver at the current level can advance it or request changes.'
      ),
      step(
        '[data-tour="collaborate-progress"]',
        'Follow the journey',
        'Open the Workflow journey to see when the period was triggered, answered, sent for approval, approved or returned for changes.',
        kb.workflows
      )
    ];
  }

  if (pathname === '/sstra') {
    return [
      step(
        '[data-tour="strategies-page"]',
        'Strategy overview',
        'Monitor every Strategy, what needs attention and what is coming up.',
        kb.strategy
      ),
      step(
        '[data-tour="strategy-filters"]',
        'Search, filter and sort',
        'Narrow the dashboard by status, attention rule, assigned Member or Business Unit, then clear filters when finished.'
      ),
      step(
        '[data-tour="strategy-list"]',
        'Strategy control panels',
        'Each panel combines status, Signal summary, attention rules and the Deliverable progress timeline. Select an issue to open that Strategy already filtered for action.'
      )
    ];
  }

  if (pathname.startsWith('/sstra/')) {
    return [
      step(
        '[data-tour="strategy-details-page"]',
        'Strategy details',
        'Manage this Strategy’s Actions, Deliverables, Signals, responsibilities and progress.',
        kb.strategyDetails
      ),
      step(
        '[data-tour="strategy-details-header"]',
        'Strategy information',
        'Review the category, owner, status, dates, introduction and cover artwork.'
      ),
      step(
        '[data-tour="strategy-details-content"]',
        'Actions and Deliverables',
        'Open an Action to inspect its Deliverables and Signals. Attention links may open this page with filters already applied.'
      ),
      step(
        '[data-tour="strategy-details-content"]',
        'Deliverable Workflows',
        'Edit a Deliverable and open Workflows to group Responders and Approvers, choose a response rule and assign the Signal periods it manages. Workflows replace Business Units for current response and approval routing.',
        kb.workflows
      ),
      step(
        '[data-tour="strategy-details-content"]',
        'Response and approval rules',
        'Without Approvers, a Workflow can wait for every Responder or finish when any one Responder answers. With approval levels, Responders share one response and any one Approver at each level advances it.'
      )
    ];
  }

  if (pathname.startsWith('/report/our_journey')) {
    return [
      step(
        '[data-tour="insights-page"]',
        'Insights dashboard',
        'Explore Strategy progress, evidence and outcomes for decision-making.',
        kb.insights
      ),
      step(
        '[data-tour="insights-timeframe"]',
        'Strategy timeframe',
        'Choose the Strategy and see where today sits within its planned dates.'
      ),
      step(
        '[data-tour="insights-progress"]',
        'Deliverable progress',
        'Select a status slice to filter the evidence and detail shown below.'
      ),
      step(
        '[data-tour="insights-explore"]',
        'Explore insights',
        'Choose Overview or a Focus Area. Clear dashboard filters before starting a different question.'
      )
    ];
  }

  if (pathname.startsWith('/admin/subscription')) {
    return [
      step(
        '[data-tour="subscription-page"]',
        'Subscription settings',
        'Manage your organisation, Members, engagement settings and reporting options.',
        kb.subscription
      ),
      step(
        '[data-tour="subscription-tabs"]',
        'Settings areas',
        'Choose Organisation, Members, Business Units or Reporting. Available areas depend on your access.'
      ),
      step(
        '[data-tour="subscription-content"]',
        'Selected settings',
        'Review the selected area carefully. Changes here can affect who receives Signals and how Strategies operate.'
      )
    ];
  }

  if (pathname.startsWith('/profile')) {
    return [
      step(
        '[data-tour="profile-page"]',
        'Your profile',
        'Manage your contact details, password and email preferences.',
        kb.profile
      ),
      step(
        '[data-tour="profile-details"]',
        'Personal details',
        'Keep your name, mobile number and profile picture current.'
      ),
      step(
        '[data-tour="profile-learning"]',
        'Learning and onboarding',
        'Restart the detailed tutorial at any time. Your Profile changes are separate and are not saved by restarting the tutorial.'
      )
    ];
  }

  return [
    step(
      'body',
      'Welcome to weavr',
      'Use the left navigation to open Updates, Strategy or Insights. You can restart the tutorial from your Profile.'
    )
  ];
};

export const getOnboardingSteps = (pathname: string, mode: OnboardingMode, context: OnboardingContext = {}) => {
  const isSharedEngagement = context.isSharedEngagement ?? true;
  return mode === 'quick'
    ? [
        ...commonQuickSteps(isSharedEngagement),
        pathname === '/updates' || pathname.startsWith('/updates?')
          ? step(
              '[data-tour="focus-area-heading"]',
              'Choose what to update',
              'Choose a Focus Area, then select the Deliverable or Signal you want to update.',
              kb.focusAreas,
              'left'
            )
          : pageSteps(pathname)[0]
      ]
    : pageSteps(pathname);
};

const currentChanges = (isSharedEngagement: boolean): string[] => [
  'Deliverable Workflows now replace Business Units for current Responder and approval assignments.',
  'Strategy and Insights now use the same pages for every subscription. Engagement Method only changes where scheduled responses are completed.',
  isSharedEngagement
    ? 'Shared engagement delivers assigned Workflow responses through Updates.'
    : 'Restricted engagement delivers assigned Workflow surveys through Collaborate.',
  'Workflows can require every Responder, any one Responder or one shared response. Approval levels always use one shared response.',
  'The Workflow journey shows when a period was triggered, answered, approved or returned for changes.'
];

export const getReturningItems = (returnBand: OnboardingReturnBand, isSharedEngagement: boolean): string[] => {
  const changes = currentChanges(isSharedEngagement);

  if (returnBand === 'recent') {
    return [
      'The weavr Guide now replaces the old Help launcher and gives advice for the page and work in front of you.',
      'Updates makes it easier to find a Deliverable or Signal and share prompted or additional progress.',
      'You can choose when email reminders begin and how often they repeat from your Profile.'
    ];
  }

  if (returnBand === 'moderate') {
    return [
      ...changes.slice(0, 3),
      'Strategy control panels now group attention rules, Signal summaries and Deliverable timelines.',
      'Insights has been refreshed for clearer, interactive Strategy reporting.'
    ];
  }

  return [
    'weavr now uses one consistent Strategy experience across subscriptions.',
    ...changes,
    'Updates and Collaborate now follow the same responsibility, Signal, schedule, response and reporting flow.',
    'The weavr Guide provides page-aware help and can be opened from the bottom-left navigation.'
  ];
};

export const getWhatsNewItems = (isSharedEngagement: boolean): string[] => currentChanges(isSharedEngagement);

export const getOnboardingHeading = (experience: OnboardingExperience) => {
  if (experience === 'returning') return 'Welcome back to weavr';
  if (experience === 'whatsNew') return 'What’s new in weavr';
  return 'Welcome to weavr';
};

export const getOnboardingSummary = (
  experience: OnboardingExperience,
  returnBand: OnboardingReturnBand = null,
  daysAway: number | null = null
) => {
  const absence = daysAway === null ? 'a while' : `${daysAway} days`;
  if (experience === 'returning')
    return returnBand === 'old'
      ? `You have been away for ${absence}. We will reintroduce the essentials and the main changes without making you relearn everything.`
      : returnBand === 'moderate'
        ? `You have been away for ${absence}. Here is a focused refresher on the parts of weavr that changed most.`
        : `Welcome back. Here is a quick reset on what changed while you were away for ${absence}.`;
  if (experience === 'whatsNew')
    return 'Updates, Strategy and Insights are now clearer, more interactive and easier to act from.';
  return 'Choose a quick introduction or learn the current page step by step.';
};
